import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "../../components/ui/Button";
import LoginSheet from "../../components/signup/LoginSheet";
import { useCallback, useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { gaEventPageView } from "../../tools/analytics/common.analytics";
import { Link, useHistory } from "react-router-dom";
import { useIsDesktopView } from "../../common/hooks/useIsDesktopView";
import { isIOSApp } from "../../tools/ios";
import { isAndroidApp } from "../../tools/android";
import {
  gaEventAuthMethodSelected,
  gaEventSignupSheetDismissed,
  gaEventSignupSheetOpened,
} from "../../tools/analytics/domains/onboarding.analytics";
import { useSignupOnboardingState } from "../../domains/auth/hooks/useSignupOnboardingState";
import { useSignupSheet } from "../../domains/auth/hooks/useSignupSheet";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { AnimatedAskBar } from "../../components/ui/AnimatedAskBar";
import { Logo } from "../../components/ui/Logo";
import heroBackground from "../../assets/images/welcome/hero.webp";
import doctor1 from "../../assets/images/welcome/doctor-1.webp";
import doctor2 from "../../assets/images/welcome/doctor-2.webp";
import doctor3 from "../../assets/images/welcome/doctor-3.webp";
import doctor4 from "../../assets/images/welcome/doctor-4.webp";
import doctor5 from "../../assets/images/welcome/doctor-5.webp";

const TRUSTED_BY_AVATARS = [
  doctor1,
  doctor2,
  doctor3,
  doctor4,
  doctor5,
] as const;

// `/signup` redirects here rather than to a page of its own, and says so: the
// visitor asked for the signup door, so the sheet is already up when they land.
const locationStateSchema = z.object({
  opensSignupSheet: z.boolean().default(false),
});

export default function WelcomePage() {
  const history = useHistory();
  const { t } = useTranslation();
  const { isDesktop } = useIsDesktopView();
  const { opensSignupSheet } = useLocationState(locationStateSchema);

  const { isInvitedToRoom, onboardingRoomData } = useSignupOnboardingState();

  const hadTypedEmailRef = useRef(false);

  // This sheet signs people UP: an address that already has an account belongs
  // at the login door, with the address carried over so it opens pre-filled.
  const goToLogin = useCallback(
    (email: string) => {
      history.push({ pathname: "/login", state: { email } });
    },
    [history]
  );

  const sheet = useSignupSheet({
    surface: "bottom_sheet",
    // Arriving from an invite link / QR scan, or from `/signup`: the sheet is
    // already up. The room logo inside it confirms the invite was taken into
    // account.
    initiallyOpen: isInvitedToRoom || opensSignupSheet,
    onExistingAccount: goToLogin,
    returnTo: null,
  });

  // App native (WKWebView ou Android WebView)
  const isNativeApp = isIOSApp() || isAndroidApp();

  // Navigateur mobile (Safari, Chrome mobile, etc.)
  const isMobileBrowser =
    !isNativeApp &&
    /iPhone|iPad|Android/.test(navigator.userAgent) &&
    !isDesktop;

  useEffect(() => {
    gaEventPageView(null);
    if (sheet.isOpen) {
      gaEventSignupSheetOpened({
        surface: "bottom_sheet",
        is_invited_to_room: isInvitedToRoom,
      });
    }
  }, []);

  const handleGetStarted = useCallback(() => {
    hadTypedEmailRef.current = false;
    gaEventSignupSheetOpened({
      surface: "bottom_sheet",
      is_invited_to_room: isInvitedToRoom,
    });
    sheet.open();
  }, [isInvitedToRoom, sheet]);

  const handleSheetClose = useCallback(() => {
    gaEventSignupSheetDismissed({
      had_typed_email: hadTypedEmailRef.current,
    });
    sheet.close();
  }, [sheet]);

  const handleLoginClick = useCallback(() => {
    sheet.close();
    history.push("/login");
  }, [history, sheet]);

  const handleQRCodeScan = useCallback(() => {
    gaEventAuthMethodSelected({
      method: "qr_code",
      intent: "signup",
      surface: "bottom_sheet",
    });
    sheet.close();
    history.push("/signup/qrcode");
  }, [history, sheet]);

  return (
    <div className="relative size-full overflow-hidden bg-white md:mx-auto md:w-full md:max-w-lg">
      <div
        className={cn(
          "flex size-full flex-col gap-3 overflow-y-auto px-6",
          isMobileBrowser ? "pb-24" : "pb-12"
        )}
        // `env()` directly, never `var(--safe-area-top)`: the token form of this
        // `max()` leaves the content under the iOS notch, while the direct one
        // is what every other notch-clearing header in the app ships with.
        style={{
          paddingTop: "max(env(safe-area-inset-top), 32px)",
          paddingBottom: isMobileBrowser
            ? undefined
            : "max(env(safe-area-inset-bottom), 3rem)",
        }}
      >
        {/* Hero: the promise over a full-bleed image, with the inert composer
            replica cycling through the questions the agent answers. Takes the
            slack the content block below leaves, so the image is what gives on
            short screens. Below `min-h-80` its own content would be clipped —
            past that point the column scrolls instead. */}
        <div className="relative flex min-h-80 flex-1 flex-col justify-between overflow-clip rounded-2xl px-3 py-6 short:py-4">
          <img
            src={heroBackground}
            alt=""
            className="absolute inset-0 size-full object-cover"
          />

          <Logo
            variant="no-tagline"
            tone="color"
            className="relative h-13 self-start short:h-10"
          />

          <div className="relative flex flex-col gap-3 short:gap-2">
            <h1 className="font-dm-sans text-welcome-title font-semibold text-white">
              <Trans
                i18nKey="Built for clinicians. <serif>Evidence first</serif>"
                components={{
                  // `block`: the design breaks the line before the serif clause
                  // whatever the language, instead of letting each translation
                  // wrap where it happens to fit.
                  serif: <span className="block font-petrona italic" />,
                }}
              />
            </h1>

            <AnimatedAskBar tone="on-image" size="mobile" />

            <div className="flex flex-col gap-3 short:gap-2">
              <div className="flex -space-x-3">
                {TRUSTED_BY_AVATARS.map((avatar) => (
                  <img
                    key={avatar}
                    src={avatar}
                    alt=""
                    className="size-10 rounded-full object-cover short:size-8"
                  />
                ))}
              </div>
              <p className="font-dm-sans text-ui-body font-medium text-white">
                {t("More than 35,000 verified healthcare professionals")}
              </p>
            </div>
          </div>
        </div>

        <p className="font-dm-sans text-heading-xs text-ds-slate-600">
          {t(
            "Ask a question. Juisci answers from the literature, and shows you the papers behind it."
          )}
        </p>

        <div className="flex flex-col gap-3 pt-3">
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleGetStarted}
            >
              {t("Continue")}
            </Button>
            <Button
              variant="ghost"
              size="md"
              className="w-full"
              onClick={handleLoginClick}
            >
              {t("I already have an account")}
            </Button>
          </div>

          <p className="text-caption text-center text-ds-slate-700">
            <Trans
              i18nKey="By continuing, you agree to the <termsLink>Terms of Service</termsLink> and <policyLink>Privacy Policy</policyLink>, and certify that you are a healthcare professional."
              components={{
                termsLink: (
                  <Link
                    className="font-semibold text-ds-mint-800 underline"
                    to="/terms?isPush=true&hideTitle=true"
                  />
                ),
                policyLink: (
                  <Link
                    className="font-semibold text-ds-mint-800 underline"
                    to="/policy?isPush=true&hideTitle=true"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <LoginSheet
        isOpen={sheet.isOpen}
        onClose={handleSheetClose}
        onOAuth={sheet.handleOAuth}
        onEmailSubmit={sheet.handleEmailSubmit}
        onEmailFirstTyped={() => {
          hadTypedEmailRef.current = true;
        }}
        onQRCodeScan={isNativeApp && !isInvitedToRoom ? handleQRCodeScan : null}
        invitedRoomLogoUrl={
          onboardingRoomData !== null
            ? getRoomLogoUrl(onboardingRoomData)
            : null
        }
        // The page already prints it under its own Continue button.
        displaysLegalNotice={false}
        disabled={sheet.isSigningIn}
      />
    </div>
  );
}
